import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Building2, Users, DollarSign, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock, XCircle, Shield, Search, ChevronLeft, ChevronRight, MoreVertical, Eye, Pencil, Trash2, UserX, UserCheck, Crown, Bell, Settings, Activity, BarChart3, Timer, Send, Globe, Server, Database, Zap, RefreshCw, ArrowLeft, Filter, Download, Calendar, MapPin, AlertCircle, CreditCard, LogOut, Key, ToggleLeft } from 'lucide-react';
import { API_BASE } from '@/config/api';
import { ConversionFunnelCard, InsightsCard, EngagementBreakdownCard, GeographyCard, FeatureAdoptionCard } from '../components/superadmin/AnalyticsComponents';

const API = API_BASE;
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(n || 0);

// ==================== COMPONENTS ====================
const StatCard = ({ title, value, subtitle, icon: Icon, color = "blue", trend, alert }) => {
  const colors = { blue: "bg-blue-500/10 text-blue-500", green: "bg-emerald-500/10 text-emerald-500", amber: "bg-amber-500/10 text-amber-500", red: "bg-rose-500/10 text-rose-500", purple: "bg-purple-500/10 text-purple-500" };
  return (
    <Card className={alert ? "border-rose-500 border-2" : ""}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{title}</p>
            <p className="text-xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
            {trend !== undefined && (
              <div className={`flex items-center gap-1 mt-1 text-xs ${trend >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                <span>{trend >= 0 ? '+' : ''}{trend}%</span>
              </div>
            )}
          </div>
          <div className={`p-2 rounded-lg ${colors[color]}`}><Icon className="h-4 w-4" /></div>
        </div>
      </CardContent>
    </Card>
  );
};

const StatusBadge = ({ status, daysLeft }) => {
  const config = {
    active: { label: "Active", className: "bg-emerald-100 text-emerald-800" },
    trial: { label: daysLeft !== undefined ? `Trial (${daysLeft}d)` : "Trial", className: "bg-blue-100 text-blue-800" },
    past_due: { label: "Past Due", className: "bg-amber-100 text-amber-800" },
    expired: { label: "Expired", className: "bg-rose-100 text-rose-800" },
    suspended: { label: "Suspended", className: "bg-rose-100 text-rose-800" },
    unknown: { label: "Unknown", className: "bg-gray-100 text-gray-800" }
  };
  const c = config[status] || config.unknown;
  return <Badge className={c.className}>{c.label}</Badge>;
};

const EngagementBadge = ({ engagement }) => {
  const config = {
    highly_engaged: { label: "Active", className: "bg-emerald-100 text-emerald-800" },
    engaged: { label: "Engaged", className: "bg-blue-100 text-blue-800" },
    moderate: { label: "Moderate", className: "bg-amber-100 text-amber-800" },
    at_risk: { label: "At Risk", className: "bg-orange-100 text-orange-800" },
    churning: { label: "Churning", className: "bg-rose-100 text-rose-800" },
    never_logged_in: { label: "Never", className: "bg-gray-100 text-gray-800" }
  };
  const c = config[engagement] || { label: engagement, className: "bg-gray-100 text-gray-800" };
  return <Badge variant="outline" className={c.className}>{c.label}</Badge>;
};

const HealthScore = ({ score }) => {
  const getColor = () => { if (score >= 80) return "text-emerald-500"; if (score >= 50) return "text-amber-500"; return "text-rose-500"; };
  return <span className={`font-bold ${getColor()}`}>{score}%</span>;
};

// ==================== MAIN COMPONENT ====================
export default function SuperAdminPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [tenantPage, setTenantPage] = useState(1);
  const [tenantPages, setTenantPages] = useState(0);
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantFilters, setTenantFilters] = useState({ status: '', plan: '', city: '' });
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetail, setTenantDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [extendTrialOpen, setExtendTrialOpen] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({ title: '', message: '', type: 'info', target: 'all', target_filter: {} });
  const [plans, setPlans] = useState([]);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [featureDialogOpen, setFeatureDialogOpen] = useState(false);
  const [tenantFeatures, setTenantFeatures] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [featureStats, setFeatureStats] = useState(null);
  const [conversionFunnel, setConversionFunnel] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { if (user && user.role !== 'super_admin') { toast.error('Access denied'); navigate('/dashboard'); } }, [user, navigate]);

  useEffect(() => { if (user?.role === 'super_admin') fetchDashboard(); }, [user, token]);

  useEffect(() => {
    if (activeTab === 'tenants') fetchTenants();
    if (activeTab === 'plans') fetchPlans();
    if (activeTab === 'activity') fetchActivityLogs();
    if (activeTab === 'system') fetchSystemHealth();
    if (activeTab === 'analytics') { fetchGeoData(); fetchFeatureStats(); fetchConversionFunnel(); }
  }, [activeTab, tenantPage, tenantSearch, tenantFilters]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/dashboard/overview`, { headers });
      setData(res.data);
    } catch (e) {
      try {
        const res = await axios.get(`${API}/api/platform-admin/stats`, { headers });
        setData({ tenants: { total: res.data.hotels?.total || 0 }, users: { total: res.data.users?.total || 0 }, subscriptions: res.data.subscriptions || {}, revenue: res.data.revenue || { mrr: 0, arr: 0 }, churn: { rate_percent: 0 }, conversion: { rate_percent: 0 }, trials: { active: 0, expiring_today: 0 }, danger_zone: { failed_payments_30d: 0 } });
      } catch { toast.error('Failed to load'); }
    } finally { setLoading(false); }
  };

  const fetchTenants = async () => {
    try {
      const params = { page: tenantPage, limit: 15 };
      if (tenantSearch) params.search = tenantSearch;
      if (tenantFilters.status) params.status = tenantFilters.status;
      if (tenantFilters.plan) params.plan = tenantFilters.plan;
      if (tenantFilters.city) params.city = tenantFilters.city;
      const res = await axios.get(`${API}/api/admin/tenants`, { headers, params });
      setTenants(res.data.tenants || []);
      setTenantPages(res.data.pagination?.pages || 0);
    } catch { 
      try {
        const res = await axios.get(`${API}/api/platform-admin/hotels`, { headers, params: { page: tenantPage, limit: 15 } });
        setTenants(res.data.hotels || []);
        setTenantPages(res.data.pagination?.pages || 0);
      } catch {}
    }
  };

  const fetchTenantDetail = async (tenantId) => {
    try {
      const res = await axios.get(`${API}/api/admin/tenants/${tenantId}`, { headers });
      setTenantDetail(res.data);
      setDetailOpen(true);
    } catch { toast.error('Failed to load details'); }
  };

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/plans`, { headers });
      setPlans(res.data.plans || []);
    } catch {
      setPlans([
        { id: 'starter', name: 'starter', display_name: 'Starter', price_monthly: 29, room_limit: 10, staff_limit: 3, subscriber_count: 0 },
        { id: 'professional', name: 'professional', display_name: 'Professional', price_monthly: 79, room_limit: 50, staff_limit: 10, subscriber_count: 0 },
        { id: 'premium', name: 'premium', display_name: 'Premium', price_monthly: 149, room_limit: 200, staff_limit: 50, subscriber_count: 0 }
      ]);
    }
  };

  const fetchActivityLogs = async () => { try { const res = await axios.get(`${API}/api/admin/activity-logs`, { headers, params: { limit: 30 } }); setActivityLogs(res.data.logs || []); } catch {} };
  const fetchSystemHealth = async () => { try { const res = await axios.get(`${API}/api/admin/system/health`, { headers }); setSystemHealth(res.data); } catch { setSystemHealth({ status: 'healthy', components: {} }); } };
  const fetchGeoData = async () => { try { const res = await axios.get(`${API}/api/admin/analytics/geography`, { headers }); setGeoData(res.data); } catch {} };
  const fetchFeatureStats = async () => { try { const res = await axios.get(`${API}/api/admin/analytics/features`, { headers }); setFeatureStats(res.data); } catch {} };
  const fetchConversionFunnel = async () => { try { const res = await axios.get(`${API}/api/admin/analytics/conversion-funnel`, { headers }); setConversionFunnel(res.data); } catch {} };

  const fetchTenantFeatures = async (tenantId) => {
    try {
      const res = await axios.get(`${API}/api/admin/features/${tenantId}`, { headers });
      setTenantFeatures(res.data);
      setFeatureDialogOpen(true);
    } catch { toast.error('Failed to load features'); }
  };

  const handleExtendTrial = async () => {
    if (!selectedTenant) return;
    try {
      await axios.put(`${API}/api/admin/tenants/${selectedTenant.id}/extend-trial?days=${extendDays}`, null, { headers });
      toast.success(`Trial extended by ${extendDays} days`);
      setExtendTrialOpen(false);
      fetchTenants();
    } catch { toast.error('Failed'); }
  };

  const handleSuspend = async (tenant, suspend) => {
    try {
      await axios.put(`${API}/api/admin/tenants/${tenant.id}/suspend?suspend=${suspend}`, null, { headers });
      toast.success(suspend ? 'Suspended' : 'Reactivated');
      fetchTenants();
    } catch { toast.error('Failed'); }
  };

  const handleImpersonate = async (tenant) => {
    try {
      const res = await axios.post(`${API}/api/admin/tenants/${tenant.id}/impersonate`, null, { headers });
      localStorage.setItem('impersonation_token', res.data.token);
      window.open('/dashboard?impersonate=true', '_blank');
      toast.success(`Viewing as ${tenant.name}`);
    } catch { toast.error('Failed'); }
  };

  const handleForceLogout = async (tenant) => {
    try {
      await axios.post(`${API}/api/admin/tenants/${tenant.id}/force-logout`, null, { headers });
      toast.success('All sessions invalidated');
    } catch { toast.error('Failed'); }
  };

  const handleResetPassword = async (tenant) => {
    try {
      await axios.post(`${API}/api/admin/tenants/${tenant.id}/reset-password`, null, { headers });
      toast.success('Password reset email sent');
    } catch { toast.error('Failed'); }
  };

  const handleBroadcast = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/notifications/broadcast`, broadcastForm, { headers });
      toast.success(`Sent to ${res.data.affected_users} users`);
      setBroadcastOpen(false);
      setBroadcastForm({ title: '', message: '', type: 'info', target: 'all', target_filter: {} });
    } catch { toast.error('Failed'); }
  };

  const handleFeatureToggle = async (featureKey, enabled) => {
    if (!tenantFeatures) return;
    try {
      await axios.put(`${API}/api/admin/features/${tenantFeatures.tenant_id}`, { feature_key: featureKey, enabled, reason: 'Admin override' }, { headers });
      toast.success(`Feature ${enabled ? 'enabled' : 'disabled'}`);
      fetchTenantFeatures(tenantFeatures.tenant_id);
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><RefreshCw className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2"><Shield className="h-5 w-5 text-purple-500" />Super Admin Console</h1>
              <p className="text-xs text-muted-foreground">SaaS Control Center</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchDashboard}><RefreshCw className="h-4 w-4" /></Button>
            <Button size="sm" onClick={() => setBroadcastOpen(true)}><Bell className="h-4 w-4 mr-1" />Broadcast</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 flex-wrap h-auto gap-1">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="tenants">Tenants</TabsTrigger>
            <TabsTrigger value="plans">Plans</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="activity">Audit Log</TabsTrigger>
            <TabsTrigger value="system">System</TabsTrigger>
          </TabsList>

          {/* ========== DASHBOARD ========== */}
          <TabsContent value="dashboard" className="space-y-4">
            {/* Danger Zone Alert */}
            {(data?.danger_zone?.failed_payments_30d > 0 || data?.trials?.expiring_today > 0) && (
              <Card className="border-rose-500 bg-rose-50 dark:bg-rose-950/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-rose-500" />
                    <div>
                      <p className="font-semibold text-rose-700 dark:text-rose-300">Action Required</p>
                      <p className="text-sm text-rose-600 dark:text-rose-400">
                        {data?.trials?.expiring_today > 0 && <span>{data.trials.expiring_today} trials expiring today. </span>}
                        {data?.danger_zone?.failed_payments_30d > 0 && <span>{data.danger_zone.failed_payments_30d} failed payments.</span>}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
              <StatCard title="Total Tenants" value={data?.tenants?.total || 0} subtitle={`${data?.users?.signups_7d || 0} this week`} icon={Building2} color="blue" />
              <StatCard title="MRR" value={fmt(data?.revenue?.mrr)} icon={DollarSign} color="green" />
              <StatCard title="ARR" value={fmt(data?.revenue?.arr)} icon={TrendingUp} color="purple" />
              <StatCard title="Churn Rate" value={`${data?.churn?.rate_percent || 0}%`} subtitle={`${data?.churn?.churned_30d || 0} churned`} icon={TrendingDown} color={data?.churn?.rate_percent > 5 ? "red" : "amber"} />
              <StatCard title="Conversion" value={`${data?.conversion?.rate_percent || 0}%`} subtitle={`Avg ${data?.conversion?.avg_days_to_convert || 0} days`} icon={Zap} color="green" />
              <StatCard title="Active Trials" value={data?.trials?.active || 0} subtitle={`${data?.trials?.expiring_today || 0} expiring today`} icon={Timer} color={data?.trials?.expiring_today > 0 ? "red" : "amber"} alert={data?.trials?.expiring_today > 0} />
            </div>

            {/* Danger Zone + Conversion */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="border-rose-200 dark:border-rose-800">
                <CardHeader className="pb-2"><CardTitle className="text-rose-600 flex items-center gap-2"><AlertCircle className="h-4 w-4" />Danger Zone</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between"><span className="text-sm">Failed Payments (30d)</span><Badge variant="destructive">{data?.danger_zone?.failed_payments_30d || 0}</Badge></div>
                  <div className="flex justify-between"><span className="text-sm">Past Due Accounts</span><Badge className="bg-amber-500">{data?.danger_zone?.past_due_accounts || 0}</Badge></div>
                  <div className="flex justify-between"><span className="text-sm">Grace Period</span><Badge variant="outline">{data?.danger_zone?.grace_period_accounts || 0}</Badge></div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2"><Zap className="h-4 w-4" />Trial Conversion</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between"><span className="text-sm">Conversion Rate</span><span className="font-bold text-emerald-500">{data?.conversion?.rate_percent || 0}%</span></div>
                  <div className="flex justify-between"><span className="text-sm">Converted (30d)</span><span className="font-medium">{data?.conversion?.converted_30d || 0}</span></div>
                  <div className="flex justify-between"><span className="text-sm">Drop-off Rate</span><span className="font-medium text-rose-500">{data?.conversion?.drop_off_rate || 0}%</span></div>
                  <div className="flex justify-between"><span className="text-sm">Avg Days to Convert</span><span className="font-medium">{data?.conversion?.avg_days_to_convert || 0}</span></div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2"><Clock className="h-4 w-4" />Expiring Trials</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between"><span className="text-sm text-rose-500 font-medium">Today</span><Badge variant="destructive">{data?.trials?.expiring_today || 0}</Badge></div>
                  <div className="flex justify-between"><span className="text-sm text-amber-500">In 3 Days</span><Badge className="bg-amber-500">{data?.trials?.expiring_3_days || 0}</Badge></div>
                  <div className="flex justify-between"><span className="text-sm">In 7 Days</span><Badge variant="secondary">{data?.trials?.expiring_7_days || 0}</Badge></div>
                </CardContent>
              </Card>
            </div>

            {/* Expiring Today List */}
            {data?.trials?.expiring_today_list?.length > 0 && (
              <Card className="border-amber-500">
                <CardHeader className="pb-2 bg-amber-50 dark:bg-amber-950/20"><CardTitle className="text-amber-600">Trials Expiring Today - Take Action</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {data.trials.expiring_today_list.slice(0, 5).map((t, i) => (
                      <div key={i} className="p-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium">{t.hotel?.name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{t.user?.email}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => { setSelectedTenant({ id: t.hotel?.id }); setExtendTrialOpen(true); }}>Extend</Button>
                          <Button size="sm">Send Reminder</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Plan Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle>Subscriptions by Plan</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(data?.subscriptions?.by_plan || {}).map(([plan, count]) => (
                    <div key={plan} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${plan === 'premium' ? 'bg-purple-500' : plan === 'professional' ? 'bg-blue-500' : plan === 'starter' ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                        <span className="capitalize">{plan}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{count}</Badge>
                        {plan !== 'trial' && <span className="text-xs text-muted-foreground">{fmt((data?.revenue?.by_plan?.[plan] || 0))}/mo</span>}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Revenue by Plan</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(data?.revenue?.by_plan || {}).map(([plan, amount]) => (
                    <div key={plan} className="space-y-1">
                      <div className="flex justify-between text-sm"><span className="capitalize">{plan}</span><span className="font-medium">{fmt(amount)}/mo</span></div>
                      <Progress value={(amount / Math.max(data?.revenue?.mrr || 1, 1)) * 100} className="h-2" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ========== TENANTS ========== */}
          <TabsContent value="tenants" className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search..." value={tenantSearch} onChange={(e) => setTenantSearch(e.target.value)} className="pl-9" />
              </div>
              <Select value={tenantFilters.status || 'all'} onValueChange={(v) => setTenantFilters({ ...tenantFilters, status: v === 'all' ? '' : v })}>
                <SelectTrigger className="w-[120px]"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="past_due">Past Due</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
              <Select value={tenantFilters.plan || 'all'} onValueChange={(v) => setTenantFilters({ ...tenantFilters, plan: v === 'all' ? '' : v })}>
                <SelectTrigger className="w-[120px]"><SelectValue placeholder="Plan" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="City..." value={tenantFilters.city} onChange={(e) => setTenantFilters({ ...tenantFilters, city: e.target.value })} className="w-[120px]" />
            </div>

            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 text-xs font-medium">Hotel</th>
                      <th className="text-left p-3 text-xs font-medium">Owner</th>
                      <th className="text-left p-3 text-xs font-medium">Status</th>
                      <th className="text-left p-3 text-xs font-medium">Plan</th>
                      <th className="text-left p-3 text-xs font-medium">MRR</th>
                      <th className="text-left p-3 text-xs font-medium">Usage</th>
                      <th className="text-left p-3 text-xs font-medium">Engagement</th>
                      <th className="text-left p-3 text-xs font-medium">Created</th>
                      <th className="text-right p-3 text-xs font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((t) => (
                      <tr key={t.id} className="border-t hover:bg-muted/30">
                        <td className="p-3">
                          <div className="font-medium text-sm">{t.name}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{t.city}</div>
                        </td>
                        <td className="p-3">
                          <div className="text-sm">{t.owner?.full_name || 'N/A'}</div>
                          <div className="text-xs text-muted-foreground">{t.owner?.email}</div>
                        </td>
                        <td className="p-3"><StatusBadge status={t.display_status} daysLeft={t.trial_days_remaining} /></td>
                        <td className="p-3"><Badge variant="outline" className="capitalize">{t.subscription?.plan || 'trial'}</Badge></td>
                        <td className="p-3 font-medium text-sm">{fmt(t.mrr)}</td>
                        <td className="p-3 text-xs"><div>{t.usage?.rooms || 0}/{t.usage?.room_limit || '∞'} rooms</div><div>{t.usage?.staff || 0}/{t.usage?.staff_limit || '∞'} staff</div></td>
                        <td className="p-3"><EngagementBadge engagement={t.engagement} /></td>
                        <td className="p-3 text-xs text-muted-foreground">{t.created_at ? new Date(t.created_at).toLocaleDateString() : 'N/A'}</td>
                        <td className="p-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => fetchTenantDetail(t.id)}><Eye className="h-4 w-4 mr-2" />View Details</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleImpersonate(t)}><Users className="h-4 w-4 mr-2" />Impersonate</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => { setSelectedTenant(t); setExtendTrialOpen(true); }}><Timer className="h-4 w-4 mr-2" />Extend Trial</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => fetchTenantFeatures(t.id)}><ToggleLeft className="h-4 w-4 mr-2" />Feature Flags</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleResetPassword(t)}><Key className="h-4 w-4 mr-2" />Reset Password</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleForceLogout(t)}><LogOut className="h-4 w-4 mr-2" />Force Logout</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleSuspend(t, !t.owner?.is_suspended)} className={t.owner?.is_suspended ? 'text-emerald-600' : 'text-rose-600'}>
                                {t.owner?.is_suspended ? <><UserCheck className="h-4 w-4 mr-2" />Reactivate</> : <><UserX className="h-4 w-4 mr-2" />Suspend</>}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Page {tenantPage} of {tenantPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setTenantPage(p => Math.max(1, p - 1))} disabled={tenantPage === 1}><ChevronLeft className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => setTenantPage(p => p + 1)} disabled={tenantPage >= tenantPages}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          </TabsContent>

          {/* ========== PLANS ========== */}
          <TabsContent value="plans" className="space-y-4">
            <div className="flex justify-between items-center"><h2 className="text-lg font-semibold">Subscription Plans</h2><Button onClick={() => setPlanDialogOpen(true)}>Add Plan</Button></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((p) => (
                <Card key={p.id}>
                  <CardHeader>
                    <CardTitle className="flex justify-between"><span className="capitalize">{p.display_name || p.name}</span><Badge>{p.subscriber_count || 0}</Badge></CardTitle>
                    <CardDescription>Plan MRR: {fmt(p.plan_mrr || 0)}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-center"><span className="text-3xl font-bold">€{p.price_monthly}</span><span className="text-muted-foreground">/mo</span></div>
                    <Separator />
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span>Rooms</span><span className="font-medium">{p.room_limit}</span></div>
                      <div className="flex justify-between"><span>Staff</span><span className="font-medium">{p.staff_limit}</span></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* ========== ANALYTICS ========== */}
          <TabsContent value="analytics" className="space-y-4">
            <ConversionFunnelCard conversionFunnel={conversionFunnel} />
            <InsightsCard insights={conversionFunnel?.insights} />
            <EngagementBreakdownCard engagement={conversionFunnel?.engagement_breakdown} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <GeographyCard geoData={geoData} />
              <FeatureAdoptionCard featureStats={featureStats} />
            </div>
          </TabsContent>

          {/* ========== ACTIVITY ========== */}
          <TabsContent value="activity" className="space-y-4">
            <Card>
              <CardHeader><CardTitle>Admin Activity Audit Log</CardTitle><CardDescription>Track all administrative actions</CardDescription></CardHeader>
              <CardContent>
                {activityLogs.length === 0 ? <p className="text-center text-muted-foreground py-8">No activity logs</p> : (
                  <div className="space-y-3">
                    {activityLogs.map((log, i) => (
                      <div key={i} className="flex items-start gap-3 pb-3 border-b">
                        <Activity className="h-4 w-4 mt-1 text-muted-foreground" />
                        <div className="flex-1">
                          <div className="flex justify-between"><span className="font-medium capitalize text-sm">{log.action?.replace('_', ' ')}</span><span className="text-xs text-muted-foreground">{new Date(log.timestamp).toLocaleString()}</span></div>
                          <p className="text-xs text-muted-foreground">{log.admin_email} • {log.resource_type}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== SYSTEM ========== */}
          <TabsContent value="system" className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card><CardContent className="p-4 flex items-center gap-3"><div className={`p-3 rounded-full ${systemHealth?.status === 'healthy' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}><Server className="h-5 w-5" /></div><div><p className="text-xs text-muted-foreground">System</p><p className="font-semibold capitalize">{systemHealth?.status || 'Healthy'}</p></div></CardContent></Card>
              <Card><CardContent className="p-4 flex items-center gap-3"><div className="p-3 rounded-full bg-emerald-100 text-emerald-600"><Database className="h-5 w-5" /></div><div><p className="text-xs text-muted-foreground">Database</p><p className="font-semibold">{systemHealth?.database?.size_mb || 0} MB</p></div></CardContent></Card>
              <Card><CardContent className="p-4 flex items-center gap-3"><div className="p-3 rounded-full bg-blue-100 text-blue-600"><Activity className="h-5 w-5" /></div><div><p className="text-xs text-muted-foreground">Error Rate (1h)</p><p className="font-semibold">{systemHealth?.metrics?.error_rate_1h || 0}%</p></div></CardContent></Card>
              <Card><CardContent className="p-4 flex items-center gap-3"><div className="p-3 rounded-full bg-purple-100 text-purple-600"><Users className="h-5 w-5" /></div><div><p className="text-xs text-muted-foreground">Active Sessions</p><p className="font-semibold">{systemHealth?.metrics?.active_sessions || 0}</p></div></CardContent></Card>
            </div>

            <Card>
              <CardHeader><CardTitle>Component Status</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(systemHealth?.components || {}).map(([name, info]) => (
                  <div key={name} className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${(typeof info === 'object' ? info.status : info) === 'healthy' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                    <span className="capitalize text-sm">{name.replace('_', ' ')}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* ========== DIALOGS ========== */}
      
      {/* Tenant Detail */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{tenantDetail?.hotel?.name}</DialogTitle><DialogDescription>{tenantDetail?.hotel?.city}, {tenantDetail?.hotel?.country}</DialogDescription></DialogHeader>
          {tenantDetail && (
            <div className="grid grid-cols-2 gap-4">
              <Card><CardHeader className="py-3"><CardTitle className="text-sm">Owner</CardTitle></CardHeader><CardContent className="text-sm space-y-1"><p><span className="text-muted-foreground">Name:</span> {tenantDetail.owner?.full_name}</p><p><span className="text-muted-foreground">Email:</span> {tenantDetail.owner?.email}</p><p><span className="text-muted-foreground">Phone:</span> {tenantDetail.owner?.phone || 'N/A'}</p><p><span className="text-muted-foreground">Last Login:</span> {tenantDetail.owner?.last_login ? new Date(tenantDetail.owner.last_login).toLocaleString() : 'Never'}</p></CardContent></Card>
              <Card><CardHeader className="py-3"><CardTitle className="text-sm">Subscription</CardTitle></CardHeader><CardContent className="text-sm space-y-1"><p><span className="text-muted-foreground">Plan:</span> <span className="capitalize">{tenantDetail.subscription?.plan}</span></p><p><span className="text-muted-foreground">Status:</span> {tenantDetail.subscription?.status}</p><p><span className="text-muted-foreground">Billing:</span> {tenantDetail.subscription?.billing_cycle}</p><p><span className="text-muted-foreground">MRR:</span> {fmt(tenantDetail.mrr)}</p>{tenantDetail.trial_days_remaining !== null && <p><span className="text-muted-foreground">Trial Days Left:</span> {tenantDetail.trial_days_remaining}</p>}</CardContent></Card>
              <Card><CardHeader className="py-3"><CardTitle className="text-sm">Usage</CardTitle></CardHeader><CardContent className="text-sm space-y-1"><p>Rooms: {tenantDetail.usage?.rooms?.current}/{tenantDetail.usage?.rooms?.limit || '∞'}</p><p>Staff: {tenantDetail.usage?.staff?.current}/{tenantDetail.usage?.staff?.limit || '∞'}</p><p>Guests: {tenantDetail.usage?.guests}</p><p>Reservations: {tenantDetail.usage?.reservations}</p></CardContent></Card>
              <Card><CardHeader className="py-3"><CardTitle className="text-sm">Metrics</CardTitle></CardHeader><CardContent className="text-sm space-y-1"><p>Revenue Generated: €{tenantDetail.revenue_generated}</p><p>Health Score: <HealthScore score={tenantDetail.health_score} /></p><p>Engagement: <EngagementBadge engagement={tenantDetail.engagement_score} /></p></CardContent></Card>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
            <Button onClick={() => handleImpersonate(tenantDetail?.hotel)}><Eye className="h-4 w-4 mr-2" />View as Tenant</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Trial */}
      <Dialog open={extendTrialOpen} onOpenChange={setExtendTrialOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Extend Trial</DialogTitle><DialogDescription>Extend trial for {selectedTenant?.name}</DialogDescription></DialogHeader>
          <div className="py-4"><Label>Days</Label><Select value={String(extendDays)} onValueChange={(v) => setExtendDays(Number(v))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="3">3 days</SelectItem><SelectItem value="7">7 days</SelectItem><SelectItem value="14">14 days</SelectItem><SelectItem value="30">30 days</SelectItem></SelectContent></Select></div>
          <DialogFooter><Button variant="outline" onClick={() => setExtendTrialOpen(false)}>Cancel</Button><Button onClick={handleExtendTrial}>Extend</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broadcast */}
      <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Broadcast Notification</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Title</Label><Input value={broadcastForm.title} onChange={(e) => setBroadcastForm({ ...broadcastForm, title: e.target.value })} /></div>
            <div><Label>Message</Label><Textarea value={broadcastForm.message} onChange={(e) => setBroadcastForm({ ...broadcastForm, message: e.target.value })} rows={3} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Type</Label><Select value={broadcastForm.type} onValueChange={(v) => setBroadcastForm({ ...broadcastForm, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="info">Info</SelectItem><SelectItem value="warning">Warning</SelectItem><SelectItem value="critical">Critical</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem></SelectContent></Select></div>
              <div><Label>Target</Label><Select value={broadcastForm.target} onValueChange={(v) => setBroadcastForm({ ...broadcastForm, target: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Users</SelectItem><SelectItem value="trial">Trial Users</SelectItem><SelectItem value="expired">Expired Users</SelectItem><SelectItem value="specific_city">Specific City</SelectItem><SelectItem value="specific_plan">Specific Plan</SelectItem></SelectContent></Select></div>
            </div>
            {broadcastForm.target === 'specific_city' && <div><Label>City</Label><Input value={broadcastForm.target_filter?.city || ''} onChange={(e) => setBroadcastForm({ ...broadcastForm, target_filter: { city: e.target.value } })} placeholder="Istanbul" /></div>}
            {broadcastForm.target === 'specific_plan' && <div><Label>Plan</Label><Select value={broadcastForm.target_filter?.plan || ''} onValueChange={(v) => setBroadcastForm({ ...broadcastForm, target_filter: { plan: v } })}><SelectTrigger><SelectValue placeholder="Select plan" /></SelectTrigger><SelectContent><SelectItem value="starter">Starter</SelectItem><SelectItem value="professional">Professional</SelectItem><SelectItem value="premium">Premium</SelectItem></SelectContent></Select></div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setBroadcastOpen(false)}>Cancel</Button><Button onClick={handleBroadcast}><Send className="h-4 w-4 mr-2" />Send</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feature Flags */}
      <Dialog open={featureDialogOpen} onOpenChange={setFeatureDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Feature Flags</DialogTitle><DialogDescription>Plan: {tenantFeatures?.plan}</DialogDescription></DialogHeader>
          {tenantFeatures && (
            <div className="space-y-4 py-4">
              {tenantFeatures.features?.map((f) => (
                <div key={f.key} className="flex items-center justify-between">
                  <div><p className="font-medium capitalize">{f.key.replace('_', ' ')}</p><p className="text-xs text-muted-foreground">{f.enabled_by_plan ? 'Included in plan' : 'Not in plan'}</p></div>
                  <Switch checked={f.effective_value} onCheckedChange={(v) => handleFeatureToggle(f.key, v)} />
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setFeatureDialogOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
